export type QuizQuestionKind = 'single' | 'multi' | 'image';
export type QuizOption = { code: string; label: string; note?: string; swatch?: string };
export type QuizDependency = { questionId: string; optionCodes: string[] };
export type QuizQuestion = {
  id: string;
  prompt: string;
  help: string;
  kind: QuizQuestionKind;
  required: boolean;
  maxSelections: number;
  options: QuizOption[];
  visibleWhen?: QuizDependency;
};
export type QuizConfig = {
  quizVersion: string;
  reviewStatus: 'demo' | 'formal_preview' | 'formal';
  questions: QuizQuestion[];
};

export const DEMO_QUIZ: QuizConfig = {
  quizVersion: 'gbm-container-demo-1',
  reviewStatus: 'demo',
  questions: [
    {
      id: 'q_recipient', prompt: 'Who are you creating for?',
      help: 'This only changes the next demo question.', kind: 'single', required: true, maxSelections: 1,
      options: [
        { code: 'self', label: 'For myself', note: 'A piece shaped by your own eye.' },
        { code: 'gift', label: 'As a gift', note: 'A thoughtful starting point you can still edit.' },
      ],
    },
    {
      id: 'q_self_moment', prompt: 'What kind of moment are you designing around?',
      help: 'Optional demo wording—not a diagnosis or personality reading.', kind: 'single', required: false, maxSelections: 1,
      visibleWhen: { questionId: 'q_recipient', optionCodes: ['self'] },
      options: [
        { code: 'everyday', label: 'An everyday reminder' },
        { code: 'new_chapter', label: 'A new chapter' },
        { code: 'simply_like', label: 'I simply like the ritual of making' },
      ],
    },
    {
      id: 'q_gift_expression', prompt: 'How would you like the gift to feel?',
      help: 'Optional. We do not ask you to describe the recipient.', kind: 'single', required: false, maxSelections: 1,
      visibleWhen: { questionId: 'q_recipient', optionCodes: ['gift'] },
      options: [
        { code: 'quiet', label: 'Quiet and understated' },
        { code: 'celebratory', label: 'Bright and celebratory' },
        { code: 'warm', label: 'Warm and steady' },
      ],
    },
    {
      id: 'q_visual_balance', prompt: 'Which visual balance draws you in?',
      help: 'Synthetic color studies for testing the image-choice interaction only.', kind: 'image', required: true, maxSelections: 1,
      options: [
        { code: 'soft', label: 'Soft tonal', note: 'Synthetic demo visual', swatch: 'linear-gradient(135deg,#ead9df,#cab6c7)' },
        { code: 'earth', label: 'Warm earth', note: 'Synthetic demo visual', swatch: 'linear-gradient(135deg,#e8d7bd,#9b765e)' },
        { code: 'contrast', label: 'Gentle contrast', note: 'Synthetic demo visual', swatch: 'linear-gradient(135deg,#5b4055 0 48%,#efe2c9 48%)' },
      ],
    },
    {
      id: 'q_detail_level', prompt: 'Which design details would you enjoy exploring?',
      help: 'Choose up to two, or skip this optional question.', kind: 'multi', required: false, maxSelections: 2,
      options: [
        { code: 'repetition', label: 'A calm repeating rhythm' },
        { code: 'center', label: 'One defined focal point' },
        { code: 'small_charm', label: 'A small hanging detail' },
      ],
    },
    {
      id: 'q_presence', prompt: 'How present should the bracelet feel?',
      help: 'Optional and purely visual.', kind: 'single', required: false, maxSelections: 1,
      options: [
        { code: 'subtle', label: 'Subtle' },
        { code: 'balanced', label: 'Balanced' },
        { code: 'expressive', label: 'More expressive' },
      ],
    },
    {
      id: 'q_studio_start', prompt: 'How would you like to enter the studio?',
      help: 'Synthetic starting points only; no real product recommendation.', kind: 'single', required: true, maxSelections: 1,
      options: [
        { code: 'open_canvas', label: 'Begin with an open canvas' },
        { code: 'review_choices', label: 'See two demo starting points first' },
      ],
    },
  ],
};
